import { useState } from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';
import { apiClient } from '../../../../services/apiClient';
import { brandConfig } from '../../../../config/brands';
import { useToast } from '../../../../hooks/useToast';
import { useUser } from '../../../../features/auth/useUser';

interface GoogleAdsAccount {
  id: string;
  account_id: string;
  account_display_name: string | null;
  is_manager: boolean;
  status: string;
}

interface Params {
  materialId: string;
  isTemporaryMaterial: boolean;
  /** 画像素材か (Google 広告は画像のみ対応) */
  isImageMaterial: boolean;
}

export interface GoogleAdsPickerProps {
  isOpen: boolean;
  accounts: GoogleAdsAccount[];
  isUploading: boolean;
  onSelect: (accountId: string) => void;
  onClose: () => void;
}

export interface GoogleAdsAssetUpload {
  /** ドロップダウン項目のクリックハンドラ */
  trigger: () => void;
  /** アップロード送信中 */
  isPending: boolean;
  /** アカウント選択モーダルへ渡す props */
  pickerProps: GoogleAdsPickerProps;
}

/**
 * 素材を Google 広告のアセットライブラリへアップロードする一連の処理。
 *
 * アカウントが 0 件: 連携誘導トースト / 1 件: 直接アップロード /
 * 2 件以上: ピッカーモーダルを開いて選ばせる。
 */
export function useGoogleAdsAssetUpload({
  materialId,
  isTemporaryMaterial,
  isImageMaterial,
}: Params): GoogleAdsAssetUpload {
  const toast = useToast();
  const { isAdmin } = useUser();
  const [pickerOpen, setPickerOpen] = useState(false);

  // 接続済み Google 広告アカウント一覧 (アップロード対象選択用)
  const accountsQuery = useQuery<{
    platform: string;
    connected: boolean;
    accounts: GoogleAdsAccount[];
  }>({
    queryKey: ['material-footer-google-ads-accounts'],
    queryFn: async () => {
      const res = await apiClient.get('/ad-connect/credentials/google_ads');
      return res.data as never;
    },
    staleTime: 60_000,
    enabled: isImageMaterial && isAdmin && brandConfig.features.googleIntegration,
  });

  // MCC manager は upload 対象にならないので除外
  const uploadableAccounts = (accountsQuery.data?.accounts ?? []).filter(
    (a) => !a.is_manager && a.status === 'active',
  );

  const uploadMutation = useMutation({
    mutationFn: async (accountId: string) => {
      const res = await apiClient.post<{
        platform: string;
        resource_name: string;
        asset_id: string;
        account_id: string;
      }>('/ad-connect/assets/upload', {
        platform: 'google_ads',
        account_id: accountId,
        material_id: materialId,
        source_table: isTemporaryMaterial ? 'temporary_materials' : 'materials',
      });
      return res.data;
    },
    onSuccess: (data) => {
      // Google Ads の web UI は API の customer_id とは別の難読化 ID (ocid 等) で
      // ルーティングされるため、特定アセットへの deep link は作れない。
      // アセットライブラリのトップへのリンクを提供し、asset_id をコピー可能な形で表示する。
      toast.show(
        <span>
          Google 広告 (customer {data.account_id}) にアップロードしました (asset_id:{' '}
          <code style={{ fontFamily: 'ui-monospace, monospace' }}>{data.asset_id}</code>)
          {' '}
          <a
            href="https://ads.google.com/aw/assetlibrary"
            target="_blank"
            rel="noreferrer"
            style={{ textDecoration: 'underline' }}
          >
            アセットライブラリを開く ↗
          </a>
        </span>,
        10000,
      );
    },
    onError: (err: unknown) => {
      const status =
        typeof err === 'object' && err && 'response' in err
          ? (err as { response?: { status?: number } }).response?.status
          : undefined;
      const message =
        typeof err === 'object' && err && 'response' in err
          ? (err as { response?: { data?: { error?: string } } }).response?.data?.error
          : undefined;
      if (status === 401) {
        // ToastProvider は Router の外側でマウントされているため <Link> を使うと
        // ナビゲーションが効かない。プレーン <a> でフルリロード遷移にする。
        toast.error(
          <span>
            {message ?? 'Google 広告アカウントが連携されていません。'}{' '}
            <a href="/connections/google-ads">接続管理を開く ↗</a>
          </span>,
        );
      } else {
        toast.error(message ?? 'Google 広告へのアップロードに失敗しました');
      }
    },
  });

  const uploadTo = (accountId: string) => {
    if (!isImageMaterial) {
      toast.error('Google 広告は画像素材のみ対応しています');
      return;
    }
    uploadMutation.mutate(accountId);
  };

  const trigger = () => {
    if (!isImageMaterial) {
      toast.error('Google 広告は画像素材のみ対応しています');
      return;
    }
    // 非 admin はアップロードできない (バックエンドが require_admin のため)。
    // 「永久に使えない」という誤解を生まないよう、組織管理者に依頼する選択肢を提示する。
    if (!isAdmin) {
      toast.show(
        <span>
          Google 広告へのアップロードにはAPI接続の設定が必要です。組織管理者にご依頼ください。{' '}
          <a href="/connections/google-ads">接続管理を開く ↗</a>
        </span>,
        8000,
      );
      return;
    }
    if (uploadableAccounts.length === 0) {
      toast.error(
        <span>
          Google 広告アカウントが連携されていません。{' '}
          <a href="/connections/google-ads">接続管理を開く ↗</a>
        </span>,
      );
      return;
    }
    if (uploadableAccounts.length === 1) {
      uploadMutation.mutate(uploadableAccounts[0].account_id);
      return;
    }
    setPickerOpen(true);
  };

  return {
    trigger,
    isPending: uploadMutation.isPending,
    pickerProps: {
      isOpen: pickerOpen,
      accounts: uploadableAccounts,
      isUploading: uploadMutation.isPending,
      onSelect: (accountId: string) => {
        setPickerOpen(false);
        uploadTo(accountId);
      },
      onClose: () => setPickerOpen(false),
    },
  };
}
